import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Shield, Users, Banknote, Key, LogOut, User, 
  ChevronRight, Home, Lightbulb, CheckCircle, Lock, Circle,
  ArrowRight, Sparkles
} from "lucide-react";
import { API } from "@/App";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TenUResLogo = ({ size = "text-4xl" }) => (
  <span className={`tenures-logo ${size}`}>
    Ten<span className="tenures-ur font-extrabold">UR</span>es
  </span>
);

// Insight Cards based on user profile
const getInsightCards = (user) => {
  const insights = [];
  
  // Unlock insight
  if (user?.tenancy_start_date) {
    const startDate = new Date(user.tenancy_start_date);
    const years = Math.floor((new Date() - startDate) / (365.25 * 24 * 60 * 60 * 1000));
    const isLondon = ['E', 'N', 'NW', 'SE', 'SW', 'W', 'WC', 'EC'].includes(user?.postcode_district);
    const cap = isLondon ? '£136,400' : '£102,400';
    
    insights.push({
      id: 'unlock',
      icon: Key,
      title: "Right to Buy",
      content: `Did you know? Your Right to Buy discount caps at ${cap} in ${isLondon ? 'London' : 'England'}. With ${years} years tenancy, we're tracking your progress toward this limit.`,
      color: "purple"
    });
  }
  
  // Exchange insight
  insights.push({
    id: 'exchange',
    icon: Users,
    title: "Smart Matching",
    content: "90% of successful swaps happen because of 'Liveability' matches, not just bedroom counts. Our engine prioritizes how you actually live.",
    color: "blue"
  });
  
  // Succession insight
  insights.push({
    id: 'succession',
    icon: Shield,
    title: "Succession Rights",
    content: "Succession rights are often a one-time event. TenURes helps you understand if your right is already 'exhausted' or still available for your family.",
    color: "emerald"
  });
  
  // Downsizing insight
  if (user?.current_bedrooms > user?.needed_bedrooms) {
    const spareBeds = user.current_bedrooms - user.needed_bedrooms;
    insights.push({
      id: 'downsizing',
      icon: Banknote,
      title: "Downsizing Bonus",
      content: `You have ${spareBeds} spare bedroom${spareBeds > 1 ? 's' : ''}. Local authorities typically offer £1,000-£2,000 per bedroom you give up. That's potential savings of £${spareBeds * 1500} or more!`,
      color: "amber"
    });
  }
  
  return insights;
};

// Journey steps calculation
const getJourneySteps = (user) => {
  const steps = [
    {
      id: 1,
      title: "Passport Created",
      description: "Your TenURes account is active",
      status: "complete"
    },
    {
      id: 2,
      title: "Profile Complete",
      description: "Property details saved",
      status: user?.onboarding_complete ? "complete" : "current"
    },
    {
      id: 3,
      title: "Triage Assessment",
      description: "Complete any module questionnaire",
      status: user?.onboarding_complete ? "current" : "locked"
    },
    {
      id: 4,
      title: "Discussion Guide Downloaded",
      description: "Get your officer-ready PDF",
      status: "locked"
    },
    {
      id: 5,
      title: "Result Logged",
      description: "Track your housing outcome",
      status: "locked"
    }
  ];
  
  return steps;
};

const InsightCard = ({ insight, delay }) => {
  const colorClasses = {
    purple: "border-purple-300 bg-purple-50",
    blue: "border-blue-300 bg-blue-50",
    emerald: "border-emerald-300 bg-emerald-50",
    amber: "border-amber-300 bg-amber-50"
  };
  
  const iconColorClasses = {
    purple: "bg-purple-100 text-purple-600",
    blue: "bg-blue-100 text-blue-600",
    emerald: "bg-emerald-100 text-emerald-600",
    amber: "bg-amber-100 text-amber-600"
  };
  
  const Icon = insight.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay }}
    >
      <Card className={`border-2 ${colorClasses[insight.color]} shadow-sm`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg ${iconColorClasses[insight.color]} flex items-center justify-center shrink-0`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Lightbulb className="w-3 h-3 text-amber-500" />
                <span className="text-xs font-semibold text-gray-500 uppercase">Did you know?</span>
              </div>
              <p className="text-sm text-[#111827] leading-relaxed">{insight.content}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const JourneyStep = ({ step, isLast }) => {
  const statusIcons = {
    complete: <CheckCircle className="w-6 h-6 text-[#05D38A]" />,
    current: <Circle className="w-6 h-6 text-[#05D38A] fill-[#05D38A]/20" />,
    locked: <Lock className="w-5 h-5 text-gray-300" />
  };
  
  const statusClasses = {
    complete: "text-[#111827]",
    current: "text-[#111827]",
    locked: "text-gray-400"
  };
  
  return (
    <div className="flex items-start gap-4">
      <div className="flex flex-col items-center">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          step.status === 'locked' ? 'bg-gray-100' : 'bg-[#05D38A]/10'
        }`}>
          {statusIcons[step.status]}
        </div>
        {!isLast && (
          <div className={`w-0.5 h-12 ${
            step.status === 'complete' ? 'bg-[#05D38A]' : 'bg-gray-200'
          }`} />
        )}
      </div>
      <div className="pt-1">
        <h4 className={`font-semibold ${statusClasses[step.status]}`}>{step.title}</h4>
        <p className="text-sm text-gray-500">{step.description}</p>
        {step.status === 'current' && (
          <span className="inline-flex items-center gap-1 mt-1 text-xs font-medium text-[#05D38A]">
            <Sparkles className="w-3 h-3" /> In Progress
          </span>
        )}
      </div>
    </div>
  );
};

const PillarCard = ({ icon: Icon, title, subtitle, description, color, onClick, delay, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
  >
    <Card 
      className="pillar-card cursor-pointer h-full"
      onClick={onClick}
      data-testid={testId}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#05D38A] transition-colors" />
        </div>
        <CardTitle className="text-xl font-bold text-[#111827] mt-4">
          {title}
        </CardTitle>
        <CardDescription className="text-[#05D38A] font-medium text-sm">
          {subtitle}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
      </CardContent>
    </Card>
  </motion.div>
);

export default function Dashboard({ user, setUser }) {
  const navigate = useNavigate();
  const [currentInsightIndex, setCurrentInsightIndex] = useState(0);
  
  const insights = getInsightCards(user);
  const journeySteps = getJourneySteps(user);
  
  // Rotate insights every 8 seconds
  useEffect(() => {
    if (insights.length > 1) {
      const interval = setInterval(() => {
        setCurrentInsightIndex((prev) => (prev + 1) % insights.length);
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [insights.length]);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/logout`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
    } catch (error) {
      // Continue with logout even if API fails
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('auth_type');
    setUser(null);
    toast.success("Signed out successfully");
    navigate('/');
  };

  const pillars = [
    {
      icon: Shield,
      title: "Succession",
      subtitle: "The Right to Pass it On",
      description: "Protect your family's right to stay. Understand succession rules and prepare your case.",
      color: "bg-emerald-100 text-emerald-600",
      path: "/succession",
      testId: "pillar-succession"
    },
    {
      icon: Users,
      title: "Mutual Exchange",
      subtitle: "Swap & Move",
      description: "Find anonymous matches based on lifestyle. Connect safely with other residents.",
      color: "bg-blue-100 text-blue-600",
      path: "/exchange",
      testId: "pillar-exchange"
    },
    {
      icon: Banknote,
      title: "Downsizing",
      subtitle: "Claim Your Bonus",
      description: "Discover cash incentives for rightsizing. See what you could claim.",
      color: "bg-amber-100 text-amber-600",
      path: "/downsizing",
      testId: "pillar-downsizing"
    },
    {
      icon: Key,
      title: "Unlock",
      subtitle: "Path to Ownership",
      description: "Track your Right to Buy progress. Calculate your discount and next steps.",
      color: "bg-purple-100 text-purple-600",
      path: "/unlock",
      testId: "pillar-unlock"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <TenUResLogo size="text-2xl" />
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 text-[#111827] hover:text-[#05D38A]" data-testid="user-menu-btn">
                <div className="w-8 h-8 rounded-full bg-[#05D38A]/10 flex items-center justify-center">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <User className="w-4 h-4 text-[#05D38A]" />
                  )}
                </div>
                <span className="hidden sm:inline font-medium">{user?.name?.split(' ')[0]}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 bg-white border-gray-200" align="end">
              <DropdownMenuLabel className="text-gray-500">
                <div className="flex flex-col">
                  <span className="text-[#111827] font-medium">{user?.name}</span>
                  <span className="text-xs text-[#05D38A] font-mono">{user?.ur_id}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-gray-200" />
              <DropdownMenuItem 
                className="text-gray-600 focus:text-[#05D38A] focus:bg-[#05D38A]/5 cursor-pointer"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-2">
              <Home className="w-5 h-5 text-[#05D38A]" />
              <span className="text-[#05D38A] font-mono text-sm font-medium">{user?.ur_id}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-2">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-gray-600">
              Your <TenUResLogo size="text-lg" /> passport is ready. What would you like to explore?
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content - Left Column */}
            <div className="lg:col-span-2 space-y-8">
              {/* Insight Card - Rotating */}
              {insights.length > 0 && (
                <div>
                  <InsightCard 
                    key={insights[currentInsightIndex].id}
                    insight={insights[currentInsightIndex]} 
                    delay={0}
                  />
                  {insights.length > 1 && (
                    <div className="flex justify-center gap-2 mt-3">
                      {insights.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentInsightIndex(index)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === currentInsightIndex ? 'bg-[#05D38A]' : 'bg-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 4 Pillar Grid */}
              <div className="bento-grid" data-testid="dashboard-pillars">
                {pillars.map((pillar, index) => (
                  <PillarCard
                    key={pillar.title}
                    {...pillar}
                    onClick={() => navigate(pillar.path)}
                    delay={index * 0.1}
                  />
                ))}
              </div>

              {/* Property Details */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="bg-white rounded-xl p-6 border border-gray-200"
              >
                <h2 className="text-lg font-bold text-[#111827] mb-4">
                  Your Property Details
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-gray-500 text-sm">Property Type</p>
                    <p className="text-[#111827] font-medium capitalize">{user?.property_type || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Current Bedrooms</p>
                    <p className="text-[#111827] font-medium">{user?.current_bedrooms || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Postcode District</p>
                    <p className="text-[#111827] font-medium">{user?.postcode_district || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Tenancy Start</p>
                    <p className="text-[#111827] font-medium">
                      {user?.tenancy_start_date ? new Date(user.tenancy_start_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '-'}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right Sidebar - Journey Tracker */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Card className="bg-white border-gray-200 sticky top-24">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-[#05D38A]" />
                      <CardTitle className="text-lg text-[#111827]">Your Journey</CardTitle>
                    </div>
                    <CardDescription>
                      Track your progress toward housing security
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-0">
                      {journeySteps.map((step, index) => (
                        <JourneyStep 
                          key={step.id} 
                          step={step} 
                          isLast={index === journeySteps.length - 1}
                        />
                      ))}
                    </div>
                    
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-500 mb-3">
                        Complete a triage assessment to unlock your next step.
                      </p>
                      <Button 
                        className="w-full bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold"
                        onClick={() => navigate('/succession')}
                      >
                        Start Assessment
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 px-4 border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            TenURes is an advisory triage tool. Final eligibility determined by your Housing Officer.
          </p>
        </div>
      </footer>
    </div>
  );
}
